<script>
import { mapGetters } from 'vuex'

export default {
    name: 'Nav',
    computed: {
        ...mapGetters(['userResource', 'locationSelected', 'branding', 'isStaff', 'isApexClub', 'pageResourceNavLinks', 'permissionsModels']),
        homeURL() {
            const siteURL = this.$appConfig('MARKETING_SITE_URL', false) || (this.locationSelected && this.locationSelected.attributes.marketing_site_url)
            return siteURL || '/'
        },
        logoSrc() {
            return this.$branding.storeVal('design_logo_image_url', 'https://drivesims-assets-public.s3.us-west-1.amazonaws.com/images/shiftarcade-logo-web.png')
        },
        logoWidth() {
            return this.$branding.storeVal('design_logo_image_width')
        },
        hasElevatedPermissions() {
            if (!this.isStaff) {
                return false
            }
            const elevated = ['leaderboards.can_manage_race_events', 'reservations.change_reservation']
            return this.permissionsModels.some((perm) => elevated.includes(perm))
        },
        extraNavLinks() {
            return this.$appConfig('NAV_LINKS', []) || []
        },
    },
    methods: {
        logout() {
            this.$store.commit('logout')
            window.location.href = '/signup'
        },
    },
}
</script>

<template>
    <header class="shell" v-if="$route.query.nav !== 'false'">
        <router-link :to="{ name: 'dashboard' }" class="shell__brand" v-if="userResource">
            <img :src="logoSrc" :width="logoWidth" :alt="$appConfig('COMPANY_NAME', 'Shift Arcade')" :title="`ShiftOS v${$appConfig('VERSION')}`" />
        </router-link>
        <a :href="homeURL" class="shell__brand" v-else>
            <img :src="logoSrc" :width="logoWidth" :alt="$appConfig('COMPANY_NAME', 'Shift Arcade')" />
        </a>

        <nav class="shell__nav">
            <template v-if="userResource">
                <router-link :to="{ name: 'dashboard' }" class="shell__link" exact>Grid</router-link>
                <router-link :to="{ name: 'leaderboards' }" class="shell__link">Boards</router-link>
                <router-link :to="{ name: 'accountProfile' }" class="shell__link">You</router-link>
                <b-dropdown right no-caret variant="link" toggle-class="shell__link shell__link--drop" menu-class="shell__menu">
                    <template #button-content>More</template>
                    <b-dropdown-item v-for="(navItem, i) in extraNavLinks" :key="`nav-${i}`" v-bind="navItem">{{ navItem.name }}</b-dropdown-item>
                    <b-dropdown-item :to="{ name: 'availabilityCars' }">Cars &amp; Tracks</b-dropdown-item>
                    <b-dropdown-item :to="{ name: 'purchaseVoucher' }">Purchase Gift Voucher</b-dropdown-item>
                    <template v-if="hasElevatedPermissions">
                        <b-dropdown-divider v-if="extraNavLinks.length > 0" />
                        <b-dropdown-header>Admin Areas</b-dropdown-header>
                        <b-dropdown-item v-if="permissionsModels.includes('reservations.change_reservation')" :to="{ name: 'adminCalendars' }">Daily Planner</b-dropdown-item>
                        <b-dropdown-item v-if="permissionsModels.includes('leaderboards.can_manage_race_events')" :to="{ name: 'adminRaceControl' }">Race Control</b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item :to="{ name: 'display' }">Board display</b-dropdown-item>
                        <b-dropdown-item :to="{ name: 'welcome' }">Menu display</b-dropdown-item>
                    </template>
                </b-dropdown>
                <b-button :to="{ name: 'reservations' }" variant="primary" size="sm" class="shell__cta">Book Now</b-button>
            </template>
            <template v-else>
                <router-link :to="{ name: 'leaderboards' }" class="shell__link">Boards</router-link>
                <router-link :to="{ name: 'login' }" class="shell__link">Sign in</router-link>
                <b-dropdown right no-caret variant="link" toggle-class="shell__link shell__link--drop" menu-class="shell__menu">
                    <template #button-content>More</template>
                    <b-dropdown-item :to="{ name: 'availabilityCars' }">Cars &amp; Tracks</b-dropdown-item>
                    <b-dropdown-item v-for="(navItem, i) in extraNavLinks" :key="`nav-${i}`" v-bind="navItem">{{ navItem.name }}</b-dropdown-item>
                </b-dropdown>
                <b-button :to="{ name: 'reservations' }" variant="primary" size="sm" class="shell__cta">Book Now</b-button>
            </template>
        </nav>
    </header>
</template>

<style lang="scss">
.shell {
    display: flex;
    align-items: center;
    gap: 28px;
    padding: 14px var(--page-x);
    background: var(--nav-bg);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    position: sticky;
    top: 0;
    z-index: 1030;
}

.shell__brand {
    display: inline-flex;
    align-items: center;
    flex: none;

    img {
        display: block;
        max-height: 80px;
    }
}

.shell__nav {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.shell__link {
    font-family: var(--font-display);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-size: 13px;
    color: var(--nav-link);
    padding: 10px 14px;
    border-bottom: 2px solid transparent;
    background: transparent;
    border-top: 0;
    border-left: 0;
    border-right: 0;
    transition: color 0.12s ease, border-color 0.12s ease;

    &:hover,
    &:focus {
        color: var(--nav-link-hover);
        text-decoration: none;
    }

    &.router-link-active {
        color: var(--nav-link-hover);
        border-bottom-color: var(--action);
    }
}

.shell__link--drop {
    text-decoration: none !important;
}

.shell__menu {
    background: var(--panel);
    border-color: var(--line-2);
}

.shell__cta {
    margin-left: 10px;
    white-space: nowrap;
}

@media (max-width: 720px) {
    .shell {
        gap: 14px;
        padding: 12px 18px;
        flex-wrap: wrap;
    }
    .shell__nav {
        gap: 2px;
        width: 100%;
    }
    .shell__link {
        padding: 8px 10px;
        letter-spacing: 0.1em;
    }
    .shell__cta {
        margin-left: auto;
    }
}
</style>
